/**
 * Wave 5 — Addenda panel with incorporation workflow.
 */
import React, { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Form,
  Input,
  List,
  Space,
  Switch,
  Tag,
  Typography,
  message,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { epcCommercialService } from '@/services/epcCommercial.service';
import { formatDate } from '@/utils/format';
import { buildAddendumIncorporation } from './clarificationAddendaNegotiationUx';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

export type EpcAddendaPanelProps = {
  tenderId: string;
  rows: Record<string, unknown>[];
  onUpdated?: () => void;
};

export const EpcAddendaPanel: React.FC<EpcAddendaPanelProps> = ({
  tenderId,
  rows,
  onUpdated,
}) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();

  const createM = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      epcCommercialService.createAddendum(values),
    onSuccess: () => {
      message.success('Addendum created');
      setOpen(false);
      form.resetFields();
      qc.invalidateQueries({ queryKey: ['epc-tender-workspace', tenderId] });
      onUpdated?.();
    },
    onError: (e: Error) => message.error(e.message || 'Create failed'),
  });

  const patchM = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) =>
      epcCommercialService.patchAddendum(id, payload),
    onSuccess: () => {
      message.success('Addendum updated');
      qc.invalidateQueries({ queryKey: ['epc-tender-workspace', tenderId] });
      onUpdated?.();
    },
    onError: (e: Error) => message.error(e.message || 'Update failed'),
  });

  const pending = rows.filter((r) => !buildAddendumIncorporation(r).incorporated).length;

  return (
    <Card
      size="small"
      title={
        <Space wrap>
          <span>Addenda</span>
          {pending > 0 ? <Tag color="error">{pending} not incorporated</Tag> : null}
        </Space>
      }
      extra={
        <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setOpen((v) => !v)}>
          New
        </Button>
      }
    >
      <Paragraph type="secondary" style={{ marginTop: 0 }}>
        Client-issued changes. Mark <Text strong>Incorporated</Text> only after estimate/proposal
        revision (operator process).
      </Paragraph>

      {!rows.length ? (
        <Alert type="info" showIcon message="No addenda yet" />
      ) : (
        <List
          size="small"
          dataSource={rows}
          renderItem={(row) => {
            const inc = buildAddendumIncorporation(row);
            const id = String(row.id);
            return (
              <List.Item
                actions={[
                  inc.canMarkIncorporated ? (
                    <Button
                      key="inc"
                      type="link"
                      loading={patchM.isPending}
                      onClick={() =>
                        patchM.mutate({
                          id,
                          payload: {
                            status: 'Incorporated',
                            metaJson: {
                              incorporated: true,
                              incorporated_at: new Date().toISOString(),
                            },
                          },
                        })
                      }
                    >
                      Mark incorporated
                    </Button>
                  ) : (
                    <Tag key="ok" color="success">
                      Done
                    </Tag>
                  ),
                ]}
              >
                <List.Item.Meta
                  title={
                    <Space wrap>
                      <Text strong>{String(row.addendum_number || id.slice(0, 8))}</Text>
                      <Tag>{String(row.status || 'Issued')}</Tag>
                      {inc.badges.map((b) => (
                        <Tag key={b.key} color={b.color}>
                          {b.label}
                        </Tag>
                      ))}
                    </Space>
                  }
                  description={
                    <Space direction="vertical" size={0}>
                      <Text>{String(row.title || '—')}</Text>
                      {row.summary ? <Text type="secondary">{String(row.summary)}</Text> : null}
                      <Text type="secondary">
                        Issued:{' '}
                        {row.issued_at ? formatDate(String(row.issued_at)) : '—'}
                      </Text>
                      <Space>
                        <Text type="secondary">Affects estimate</Text>
                        <Switch
                          size="small"
                          checked={inc.affectsEstimate}
                          disabled={patchM.isPending || inc.incorporated}
                          onChange={(affectsEstimate) =>
                            patchM.mutate({ id, payload: { affectsEstimate } })
                          }
                        />
                      </Space>
                      {inc.reasons.length ? (
                        <Text type="secondary">{inc.reasons.join(' · ')}</Text>
                      ) : null}
                    </Space>
                  }
                />
              </List.Item>
            );
          }}
        />
      )}

      {open ? (
        <Card size="small" title="New addendum" style={{ marginTop: 12 }}>
        <Form
          form={form}
          layout="vertical"
          initialValues={{ affectsEstimate: true }}
          onFinish={(values) => {
            createM.mutate({
              tenderId,
              title: values.title,
              summary: values.summary,
              affectsEstimate: Boolean(values.affectsEstimate),
            });
          }}
        >
          <Form.Item name="title" label="Title" rules={[{ required: true, message: 'Required' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="summary" label="Summary">
            <TextArea rows={3} />
          </Form.Item>
          <Form.Item name="affectsEstimate" valuePropName="checked">
            <Checkbox>Affects estimate / proposal (requires incorporation)</Checkbox>
          </Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={createM.isPending}>
              Save
            </Button>
            <Button
              onClick={() => {
                setOpen(false);
                form.resetFields();
              }}
            >
              Cancel
            </Button>
          </Space>
        </Form>
        </Card>
      ) : null}
    </Card>
  );
};

export default EpcAddendaPanel;
